import { useState, useEffect } from 'react';
import { X, Calendar, Video, Phone, Clock, Loader2, CheckCircle, AlertCircle, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

const TIMEZONES = [
  { value: 'America/New_York',    label: 'Eastern (ET)' },
  { value: 'America/Chicago',     label: 'Central (CT)' },
  { value: 'America/Denver',      label: 'Mountain (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific (PT)' },
  { value: 'America/Phoenix',     label: 'Arizona (MT no DST)' },
  { value: 'America/Anchorage',   label: 'Alaska (AKT)' },
  { value: 'Pacific/Honolulu',    label: 'Hawaii (HT)' },
];

export default function ScheduleInterviewModal({ candidate, jobId, onClose, onSuccess }) {
  const [googleConnected, setGoogleConnected] = useState(null);
  const [form, setForm] = useState({
    date: '',
    time: '',
    duration: 60,
    type: 'video',
    timezone: 'America/New_York',
    notes: '',
  });
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    checkGoogleStatus();
  }, []);

  const checkGoogleStatus = async () => {
    try {
      const { data } = await api.get('/auth/google/status');
      setGoogleConnected(data.connected);
    } catch {
      setGoogleConnected(false);
    }
  };

  const connectGoogle = async () => {
    try {
      const { data } = await api.get('/auth/google/authorize');
      window.open(data.auth_url, '_blank', 'width=600,height=700');
    } catch {
      toast.error('Failed to get Google authorization URL');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.date || !form.time) {
      toast.error('Please select a date and time');
      return;
    }

    // Build ISO datetime
    const dateTimeStr = `${form.date}T${form.time}:00`;

    setSaving(true);
    try {
      const { data } = await api.post('/scheduling/create-interview', {
        candidate_id: candidate.candidate_id,
        job_id: jobId,
        date_time: dateTimeStr,
        duration_minutes: form.duration,
        interview_type: form.type,
        timezone: form.timezone,
        notes: form.notes || null,
      });
      setResult(data);
      toast.success(`Interview scheduled! ${data.email_sent ? 'Email sent to candidate.' : ''}`);
      if (onSuccess) onSuccess(data);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Failed to schedule interview';
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 flex items-center justify-center p-4"
      onClick={onClose}>
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-lg shadow-2xl"
        onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-lg font-bold text-slate-800">Schedule Interview</h2>
            <p className="text-xs text-slate-400 mt-0.5">{candidate?.name}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 p-1">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Success State */}
        {result ? (
          <div className="p-6">
            <div className="text-center py-6">
              <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-800 mb-2">Interview Scheduled!</h3>
              <p className="text-slate-500 text-sm mb-6">
                {result.email_sent ? 'Invitation email sent to candidate.' : 'Interview created.'}
              </p>
              <div className="space-y-3">
                {result.zoom_link && (
                  <a href={result.zoom_link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 justify-center px-4 py-2.5 bg-[#0e72ed] text-white rounded-xl text-sm font-semibold hover:bg-[#0b5cca]">
                    <Video className="w-4 h-4" /> Open Zoom Meeting
                  </a>
                )}
                {result.calendar_link && (
                  <a href={result.calendar_link} target="_blank" rel="noopener noreferrer"
                    className="flex items-center gap-2 justify-center px-4 py-2.5 bg-white border border-slate-200 text-slate-700 rounded-xl text-sm font-medium hover:bg-slate-50">
                    <Calendar className="w-4 h-4" /> View in Google Calendar <ExternalLink className="w-3 h-3" />
                  </a>
                )}
                <button onClick={onClose}
                  className="w-full border border-slate-200 text-slate-600 py-2.5 rounded-xl text-sm hover:bg-slate-50">
                  Close
                </button>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">

            {/* Google Calendar Status */}
            {googleConnected === false && form.type === 'video' && (
              <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-xs font-medium text-amber-800">Google Calendar not connected</p>
                  <p className="text-xs text-amber-600 mt-0.5">Connect to auto-create calendar invites</p>
                </div>
                <button type="button" onClick={connectGoogle}
                  className="text-xs text-[#2563eb] font-medium hover:underline flex-shrink-0">
                  Connect
                </button>
              </div>
            )}
            {googleConnected === true && (
              <div className="flex items-center gap-2 px-3 py-2 bg-emerald-50 border border-emerald-200 rounded-xl">
                <CheckCircle className="w-4 h-4 text-emerald-600" />
                <p className="text-xs font-medium text-emerald-800">Google Calendar connected</p>
              </div>
            )}

            {/* Interview Type */}
            <div>
              <label className="text-slate-700 text-sm font-semibold block mb-1.5">Interview Type</label>
              <div className="flex gap-2">
                {[{value:'video',icon:Video,label:'Video (Zoom)'},{value:'phone',icon:Phone,label:'Phone'}].map(opt => (
                  <button key={opt.value} type="button"
                    onClick={() => setForm({...form, type: opt.value})}
                    className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                      form.type === opt.value ? 'bg-[#2563eb] text-white border-[#2563eb]' : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-50'
                    }`}>
                    <opt.icon className="w-4 h-4" /> {opt.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Date + Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-slate-700 text-sm font-semibold block mb-1.5">Date <span className="text-red-500">*</span></label>
                <input type="date" value={form.date} min={today}
                  onChange={e => setForm({...form, date: e.target.value})}
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 text-sm focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none" />
              </div>
              <div>
                <label className="text-slate-700 text-sm font-semibold block mb-1.5">Time <span className="text-red-500">*</span></label>
                <input type="time" value={form.time}
                  onChange={e => setForm({...form, time: e.target.value})}
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 text-sm focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none" />
              </div>
            </div>

            {/* Duration + Timezone */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-slate-700 text-sm font-semibold block mb-1.5">Duration</label>
                <select value={form.duration} onChange={e => setForm({...form, duration: parseInt(e.target.value)})}
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 text-sm focus:border-[#2563eb] outline-none">
                  {[30,45,60,90].map(d => <option key={d} value={d}>{d} minutes</option>)}
                </select>
              </div>
              <div>
                <label className="text-slate-700 text-sm font-semibold block mb-1.5">Timezone</label>
                <select value={form.timezone} onChange={e => setForm({...form, timezone: e.target.value})}
                  className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 text-sm focus:border-[#2563eb] outline-none">
                  {TIMEZONES.map(tz => <option key={tz.value} value={tz.value}>{tz.label}</option>)}
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label className="text-slate-700 text-sm font-semibold block mb-1.5">Notes (optional)</label>
              <textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                rows={2} placeholder="Any preparation notes for the candidate..."
                className="w-full bg-white border border-slate-200 rounded-xl px-4 py-2.5 text-slate-800 text-sm placeholder-slate-400 focus:border-[#2563eb] focus:ring-2 focus:ring-[#2563eb]/20 outline-none resize-none" />
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-1">
              <button type="button" onClick={onClose}
                className="flex-1 border border-slate-200 text-slate-600 py-3 rounded-xl text-sm hover:bg-slate-50">Cancel</button>
              <button type="submit" disabled={saving || !form.date || !form.time}
                className="flex-1 bg-[#2563eb] hover:bg-[#1d4ed8] text-white py-3 rounded-xl text-sm font-semibold disabled:opacity-50 flex items-center justify-center gap-2">
                {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Scheduling...</> : <><Calendar className="w-4 h-4" /> Schedule Interview</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
